import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Form Section Component
function FormSection({ title, children, defaultOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-gradient-to-r from-orange-50 to-amber-50 flex justify-between items-center text-left font-medium text-gray-800"
      >
        <span>{title}</span>
        <svg className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && <div className="p-4 bg-white">{children}</div>}
    </div>
  );
}

// Input Field Component
function InputField({ label, name, value, onChange, type = "text", required = false, placeholder = "", options = null, disabled = false }) {
  const baseClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm";
  
  if (options) {
    return (
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <select 
          name={name} 
          value={value} 
          onChange={onChange} 
          className={baseClass}
          required={required}
          disabled={disabled}
        >
          <option value="">-- चुनें --</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  }
  
  if (type === "checkbox") {
    return (
      <label className="flex items-center space-x-2 cursor-pointer">
        <input
          type="checkbox"
          name={name}
          checked={value}
          onChange={onChange}
          className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
          disabled={disabled}
        />
        <span className="text-sm text-gray-700">{label}</span>
      </label>
    );
  }
  
  if (type === "textarea") {
    return (
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={baseClass}
          rows={3}
          required={required}
          disabled={disabled}
        />
      </div>
    );
  }
  
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={baseClass}
        required={required}
        disabled={disabled}
      />
    </div>
  );
}

// Main Form Component
export default function StudentRegistrationForm({ classes, onSubmit, onCancel, editStudent = null, token }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [districts, setDistricts] = useState([]);
  const [permDistricts, setPermDistricts] = useState([]);
  
  // Madhya Pradesh districts (hardcoded for performance)
  const MP_DISTRICTS = [
    "Agar Malwa", "Alirajpur", "Anuppur", "Ashoknagar", "Balaghat", "Barwani",
    "Betul", "Bhind", "Bhopal", "Burhanpur", "Chhatarpur", "Chhindwara",
    "Damoh", "Datia", "Dewas", "Dhar", "Dindori", "Guna", "Gwalior",
    "HARDA", "Harda", "Hoshangabad", "INDORE", "Indore", "Jabalpur", "Jhabua", "Katni",
    "Khandwa", "Khargone", "Mandla", "Mandsaur", "Morena", "Narsinghpur",
    "Neemuch", "Niwari", "Panna", "Raisen", "Rajgarh", "Ratlam", "Rewa",
    "Sagar", "Satna", "Sehore", "Seoni", "Shahdol", "Shajapur", "Sheopur",
    "Shivpuri", "Sidhi", "Singrauli", "Tikamgarh", "Ujjain", "Umaria", "Vidisha"
  ];
  
  // Indian States
  const INDIAN_STATES = [
    "Madhya Pradesh", "Uttar Pradesh", "Rajasthan", "Gujarat", "Maharashtra", 
    "Chhattisgarh", "Andhra Pradesh", "Bihar", "Delhi", "Goa", "Haryana", 
    "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Punjab", 
    "Tamil Nadu", "Telangana", "West Bengal", "Uttarakhand", "Odisha"
  ];
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Info
    admission_number: '',
    roll_number: '',
    scholar_number: '',
    enrollment_number: '',
    name: '',
    name_hindi: '',
    gender: 'male',
    dob: '',
    blood_group: '',
    mother_tongue: '',
    nationality: 'Indian',
    place_of_birth: '',
    birth_place_district: '',
    birth_place_state: 'Madhya Pradesh',
    
    // Class Info
    current_class_id: '',
    current_section_id: '',
    academic_year: '2025-26',
    admission_class: '',
    stream: '',
    medium: 'Hindi',
    second_language: '',
    third_language: '',
    
    // Identity & Government
    samagra_id: '',
    samagra_family_id: '',
    aadhaar_number: '',
    ration_card_number: '',
    domicile_certificate_number: '',
    
    // Category
    religion: '',
    caste: '',
    caste_category: '',
    sub_caste: '',
    is_bpl: false,
    bpl_card_number: '',
    is_minority: false,
    minority_type: '',
    is_cwsn: false,
    disability_type: '',
    disability_percentage: '',
    is_orphan: false,
    is_single_parent: false,
    single_parent_type: '',
    
    // RTE & Previous School
    rte_status: false,
    is_repeater: false,
    previous_school_name: '',
    previous_school_udise: '',
    previous_school_address: '',
    previous_class: '',
    previous_year_status: '',
    previous_year_percentage: '',
    tc_number: '',
    
    // Father Details
    father_name: '',
    father_name_hindi: '',
    father_occupation: '',
    father_education: '',
    father_phone: '',
    father_aadhaar: '',
    father_annual_income: '',
    father_alive: true,
    
    // Mother Details
    mother_name: '',
    mother_name_hindi: '',
    mother_occupation: '',
    mother_education: '',
    mother_phone: '',
    mother_aadhaar: '',
    mother_annual_income: '',
    mother_alive: true,
    
    // Guardian Details
    guardian_name: '',
    guardian_name_hindi: '',
    guardian_relation: '',
    guardian_phone: '',
    guardian_occupation: '',
    guardian_aadhaar: '',
    
    // Contact
    primary_phone: '',
    secondary_phone: '',
    email: '',
    whatsapp_number: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    
    // Address
    address_house_number: '',
    address_street_mohalla: '',
    address_village_town: '',
    address_post_office: '',
    address_police_station: '',
    address_block_tehsil: '',
    address_district: '',
    address_state: 'Madhya Pradesh',
    address_pincode: '',
    is_address_same: true,
    
    // Permanent Address
    perm_house_number: '',
    perm_street_mohalla: '',
    perm_village_town: '',
    perm_post_office: '',
    perm_police_station: '',
    perm_block_tehsil: '',
    perm_district: '',
    perm_state: 'Madhya Pradesh',
    perm_pincode: '',
    
    // Bank Details
    bank_account_holder: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_name: '',
    bank_branch: '',
    
    // Facilities
    transport_availed: false,
    transport_distance_km: '',
    transport_vehicle_number: '',
    mid_day_meal: false,
    free_textbook: false,
    free_uniform: false,
    hostel: false,
    scholarship_name: '',
    scholarship_amount: '',
    
    // Documents
    photo_uploaded: false,
    birth_certificate: false,
    birth_certificate_number: '',
    caste_certificate: false,
    caste_certificate_number: '',
    disability_certificate: false,
    disability_certificate_number: '',
    income_certificate: false,
    income_certificate_number: '',
    transfer_certificate: false,
    transfer_certificate_number: '',
    domicile_certificate: false,
    
    // Additional
    height_cm: '',
    weight_kg: '',
    identification_mark_1: '',
    identification_mark_2: '',
    allergies: '',
    medical_conditions: '',
    remarks: ''
  });

  // Load edit data
  useEffect(() => {
    if (editStudent) {
      const mappedData = { ...formData };
      Object.keys(editStudent).forEach(key => {
        if (key in mappedData) {
          mappedData[key] = editStudent[key] || mappedData[key];
        }
      });
      // Map nested address
      if (editStudent.address) {
        mappedData.address_house_number = editStudent.address.house_number || '';
        mappedData.address_street_mohalla = editStudent.address.street_mohalla || '';
        mappedData.address_village_town = editStudent.address.village_town || '';
        mappedData.address_post_office = editStudent.address.post_office || '';
        mappedData.address_police_station = editStudent.address.police_station || '';
        mappedData.address_block_tehsil = editStudent.address.block_tehsil || '';
        mappedData.address_district = editStudent.address.district || '';
        mappedData.address_state = editStudent.address.state || 'Madhya Pradesh';
        mappedData.address_pincode = editStudent.address.pincode || '';
      }
      // Map bank details
      if (editStudent.bank_details) {
        mappedData.bank_account_holder = editStudent.bank_details.account_holder_name || '';
        mappedData.bank_account_number = editStudent.bank_details.account_number || '';
        mappedData.bank_ifsc = editStudent.bank_details.ifsc_code || '';
        mappedData.bank_name = editStudent.bank_details.bank_name || '';
        mappedData.bank_branch = editStudent.bank_details.branch_name || '';
      }
      // Map facilities
      if (editStudent.facilities) {
        mappedData.transport_availed = editStudent.facilities.transport_availed || false;
        mappedData.transport_distance_km = editStudent.facilities.transport_distance_km || '';
        mappedData.mid_day_meal = editStudent.facilities.mid_day_meal || false;
        mappedData.free_textbook = editStudent.facilities.free_textbook || false;
        mappedData.free_uniform = editStudent.facilities.free_uniform || false;
        mappedData.hostel = editStudent.facilities.hostel || false;
        mappedData.scholarship_name = editStudent.facilities.scholarship_name || '';
        mappedData.scholarship_amount = editStudent.facilities.scholarship_amount || '';
      }
      // Format DOB
      if (editStudent.dob) {
        mappedData.dob = editStudent.dob.split('T')[0];
      }
      setFormData(mappedData);
    }
  }, [editStudent]);

  // Load sections when class changes
  useEffect(() => {
    if (formData.current_class_id) {
      const cls = classes.find(c => c.id === formData.current_class_id);
      setSections(cls?.sections || []);
    } else {
      setSections([]);
    }
  }, [formData.current_class_id, classes]);

  // Load districts when state changes
  useEffect(() => {
    const loadDistricts = async (state, setter) => {
      if (state === 'Madhya Pradesh') {
        setter(MP_DISTRICTS);
      } else if (state) {
        try {
          const res = await fetch(`${API_URL}/api/data-completion/districts/${encodeURIComponent(state)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json();
          setter(data.districts || []);
        } catch (err) {
          setter([]);
        }
      } else {
        setter([]);
      }
    };
    
    loadDistricts(formData.address_state, setDistricts);
  }, [formData.address_state, token]);

  // Load permanent address districts
  useEffect(() => {
    const loadPermDistricts = async () => {
      if (formData.perm_state === 'Madhya Pradesh') {
        setPermDistricts(MP_DISTRICTS);
      } else if (formData.perm_state) {
        try {
          const res = await fetch(`${API_URL}/api/data-completion/districts/${encodeURIComponent(formData.perm_state)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json();
          setPermDistricts(data.districts || []);
        } catch (err) {
          setPermDistricts([]);
        }
      } else {
        setPermDistricts([]);
      }
    };
    
    loadPermDistricts();
  }, [formData.perm_state, token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Build the submission data
    const submitData = {
      admission_number: formData.admission_number,
      roll_number: formData.roll_number || null,
      scholar_number: formData.scholar_number || null,
      enrollment_number: formData.enrollment_number || null,
      name: formData.name,
      name_hindi: formData.name_hindi || null,
      gender: formData.gender,
      dob: new Date(formData.dob).toISOString(),
      blood_group: formData.blood_group || null,
      mother_tongue: formData.mother_tongue || null,
      nationality: formData.nationality,
      place_of_birth: formData.place_of_birth || null,
      birth_place_district: formData.birth_place_district || null,
      birth_place_state: formData.birth_place_state || null,
      
      current_class_id: formData.current_class_id,
      current_section_id: formData.current_section_id,
      academic_year: formData.academic_year,
      admission_class: formData.admission_class || null,
      stream: formData.stream || null,
      medium: formData.medium,
      second_language: formData.second_language || null,
      third_language: formData.third_language || null,
      
      samagra_id: formData.samagra_id || null,
      samagra_family_id: formData.samagra_family_id || null,
      aadhaar_number: formData.aadhaar_number || null,
      ration_card_number: formData.ration_card_number || null,
      domicile_certificate_number: formData.domicile_certificate_number || null,
      
      religion: formData.religion || null,
      caste: formData.caste || null,
      caste_category: formData.caste_category || null,
      sub_caste: formData.sub_caste || null,
      is_bpl: formData.is_bpl,
      bpl_card_number: formData.bpl_card_number || null,
      is_minority: formData.is_minority,
      minority_type: formData.minority_type || null,
      is_cwsn: formData.is_cwsn,
      disability_type: formData.disability_type || null,
      disability_percentage: formData.disability_percentage ? parseInt(formData.disability_percentage) : null,
      is_orphan: formData.is_orphan,
      is_single_parent: formData.is_single_parent,
      single_parent_type: formData.single_parent_type || null,
      
      rte_status: formData.rte_status,
      is_repeater: formData.is_repeater,
      previous_school_name: formData.previous_school_name || null,
      previous_school_udise: formData.previous_school_udise || null,
      previous_school_address: formData.previous_school_address || null,
      previous_class: formData.previous_class || null,
      previous_year_status: formData.previous_year_status || null,
      previous_year_percentage: formData.previous_year_percentage ? parseFloat(formData.previous_year_percentage) : null,
      tc_number: formData.tc_number || null,
      
      father_name: formData.father_name || null,
      father_name_hindi: formData.father_name_hindi || null,
      father_occupation: formData.father_occupation || null,
      father_education: formData.father_education || null,
      father_phone: formData.father_phone || null,
      father_aadhaar: formData.father_aadhaar || null,
      father_annual_income: formData.father_annual_income ? parseFloat(formData.father_annual_income) : null,
      father_alive: formData.father_alive,
      
      mother_name: formData.mother_name || null,
      mother_name_hindi: formData.mother_name_hindi || null,
      mother_occupation: formData.mother_occupation || null,
      mother_education: formData.mother_education || null,
      mother_phone: formData.mother_phone || null,
      mother_aadhaar: formData.mother_aadhaar || null,
      mother_annual_income: formData.mother_annual_income ? parseFloat(formData.mother_annual_income) : null,
      mother_alive: formData.mother_alive,
      
      guardian_name: formData.guardian_name || null,
      guardian_name_hindi: formData.guardian_name_hindi || null,
      guardian_relation: formData.guardian_relation || null,
      guardian_phone: formData.guardian_phone || null,
      guardian_occupation: formData.guardian_occupation || null,
      guardian_aadhaar: formData.guardian_aadhaar || null,
      
      primary_phone: formData.primary_phone,
      secondary_phone: formData.secondary_phone || null,
      email: formData.email || null,
      whatsapp_number: formData.whatsapp_number || null,
      emergency_contact_name: formData.emergency_contact_name || null,
      emergency_contact_phone: formData.emergency_contact_phone || null,
      emergency_contact_relation: formData.emergency_contact_relation || null,
      
      address: {
        house_number: formData.address_house_number || null,
        street_mohalla: formData.address_street_mohalla || null,
        village_town: formData.address_village_town || '',
        post_office: formData.address_post_office || null,
        police_station: formData.address_police_station || null,
        block_tehsil: formData.address_block_tehsil || null,
        district: formData.address_district || '',
        state: formData.address_state || 'Madhya Pradesh',
        pincode: formData.address_pincode || ''
      },
      
      is_address_same: formData.is_address_same,
      
      permanent_address: !formData.is_address_same ? {
        house_number: formData.perm_house_number || null,
        street_mohalla: formData.perm_street_mohalla || null,
        village_town: formData.perm_village_town || '',
        post_office: formData.perm_post_office || null,
        police_station: formData.perm_police_station || null,
        block_tehsil: formData.perm_block_tehsil || null,
        district: formData.perm_district || '',
        state: formData.perm_state || 'Madhya Pradesh',
        pincode: formData.perm_pincode || ''
      } : null,
      
      bank_details: (formData.bank_account_number) ? {
        account_holder_name: formData.bank_account_holder || '',
        account_number: formData.bank_account_number || '',
        ifsc_code: formData.bank_ifsc || '',
        bank_name: formData.bank_name || null,
        branch_name: formData.bank_branch || null
      } : null,
      
      facilities: {
        transport_availed: formData.transport_availed,
        transport_distance_km: formData.transport_distance_km ? parseFloat(formData.transport_distance_km) : null,
        transport_vehicle_number: formData.transport_vehicle_number || null,
        mid_day_meal: formData.mid_day_meal,
        free_textbook: formData.free_textbook,
        free_uniform: formData.free_uniform,
        hostel: formData.hostel,
        scholarship_name: formData.scholarship_name || null,
        scholarship_amount: formData.scholarship_amount ? parseFloat(formData.scholarship_amount) : null
      },
      
      documents: {
        photo_uploaded: formData.photo_uploaded,
        birth_certificate: formData.birth_certificate,
        birth_certificate_number: formData.birth_certificate_number || null,
        caste_certificate: formData.caste_certificate,
        caste_certificate_number: formData.caste_certificate_number || null,
        disability_certificate: formData.disability_certificate,
        disability_certificate_number: formData.disability_certificate_number || null,
        income_certificate: formData.income_certificate,
        income_certificate_number: formData.income_certificate_number || null,
        transfer_certificate: formData.transfer_certificate,
        transfer_certificate_number: formData.transfer_certificate_number || null,
        domicile_certificate: formData.domicile_certificate,
        domicile_certificate_number: formData.domicile_certificate_number || null
      },
      
      height_cm: formData.height_cm ? parseFloat(formData.height_cm) : null,
      weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
      identification_mark_1: formData.identification_mark_1 || null,
      identification_mark_2: formData.identification_mark_2 || null,
      allergies: formData.allergies || null,
      medical_conditions: formData.medical_conditions || null,
      remarks: formData.remarks || null
    };
    
    await onSubmit(submitData);
    setLoading(false);
  };

  const tabs = [
    { name: 'मूल जानकारी', icon: '👤' },
    { name: 'पहचान व श्रेणी', icon: '📋' },
    { name: 'शैक्षणिक', icon: '📚' },
    { name: 'अभिभावक', icon: '👨‍👩‍👧' },
    { name: 'संपर्क व पता', icon: '📍' },
    { name: 'बैंक व सुविधाएं', icon: '🏦' },
    { name: 'दस्तावेज़', icon: '📄' }
  ];

  // Dropdown options
  const genderOptions = [
    { value: 'male', label: 'पुरुष (Male)' },
    { value: 'female', label: 'महिला (Female)' },
    { value: 'other', label: 'अन्य (Other)' }
  ];

  const bloodGroupOptions = [
    { value: 'A+', label: 'A+' },
    { value: 'A-', label: 'A-' },
    { value: 'B+', label: 'B+' },
    { value: 'B-', label: 'B-' },
    { value: 'AB+', label: 'AB+' },
    { value: 'AB-', label: 'AB-' },
    { value: 'O+', label: 'O+' },
    { value: 'O-', label: 'O-' }
  ];

  const religionOptions = [
    { value: 'hindu', label: 'हिन्दू (Hindu)' },
    { value: 'muslim', label: 'मुस्लिम (Muslim)' },
    { value: 'christian', label: 'ईसाई (Christian)' },
    { value: 'sikh', label: 'सिख (Sikh)' },
    { value: 'buddhist', label: 'बौद्ध (Buddhist)' },
    { value: 'jain', label: 'जैन (Jain)' },
    { value: 'other', label: 'अन्य (Other)' }
  ];

  const categoryOptions = [
    { value: 'general', label: 'सामान्य (General)' },
    { value: 'sc', label: 'अनुसूचित जाति (SC)' },
    { value: 'st', label: 'अनुसूचित जनजाति (ST)' },
    { value: 'obc', label: 'अन्य पिछड़ा वर्ग (OBC)' },
    { value: 'ews', label: 'आर्थिक रूप से कमजोर (EWS)' }
  ];

  const mediumOptions = [
    { value: 'Hindi', label: 'हिन्दी (Hindi)' },
    { value: 'English', label: 'अंग्रेजी (English)' },
    { value: 'Urdu', label: 'उर्दू (Urdu)' }
  ];

  const streamOptions = [
    { value: 'arts', label: 'कला (Arts)' },
    { value: 'science', label: 'विज्ञान (Science)' },
    { value: 'commerce', label: 'वाणिज्य (Commerce)' }
  ];

  const disabilityOptions = [
    { value: 'visual', label: 'दृष्टि बाधित (Visual)' },
    { value: 'hearing', label: 'श्रवण बाधित (Hearing)' },
    { value: 'speech', label: 'वाक् बाधित (Speech)' },
    { value: 'locomotor', label: 'चलन बाधित (Locomotor)' },
    { value: 'mental', label: 'मानसिक (Mental)' },
    { value: 'cerebral_palsy', label: 'सेरेब्रल पाल्सी (Cerebral Palsy)' },
    { value: 'autism', label: 'ऑटिज्म (Autism)' },
    { value: 'multiple', label: 'बहु-विकलांगता (Multiple)' }
  ];

  const educationOptions = [
    { value: 'illiterate', label: 'अशिक्षित' },
    { value: 'primary', label: 'प्राथमिक' },
    { value: 'middle', label: 'माध्यमिक' },
    { value: 'high_school', label: 'हाई स्कूल' },
    { value: 'higher_secondary', label: 'हायर सेकेंडरी' },
    { value: 'graduate', label: 'स्नातक' },
    { value: 'post_graduate', label: 'स्नातकोत्तर' },
    { value: 'professional', label: 'व्यावसायिक' }
  ];

  const previousStatusOptions = [
    { value: 'passed', label: 'उत्तीर्ण (Passed)' },
    { value: 'failed', label: 'अनुत्तीर्ण (Failed)' },
    { value: 'na', label: 'लागू नहीं (NA)' }
  ];

  const languageOptions = [
    { value: 'sanskrit', label: 'संस्कृत (Sanskrit)' },
    { value: 'english', label: 'अंग्रेजी (English)' },
    { value: 'hindi', label: 'हिन्दी (Hindi)' },
    { value: 'urdu', label: 'उर्दू (Urdu)' }
  ];

  const stateOptions = [
    { value: 'Madhya Pradesh', label: 'मध्य प्रदेश' },
    { value: 'Uttar Pradesh', label: 'उत्तर प्रदेश' },
    { value: 'Maharashtra', label: 'महाराष्ट्र' },
    { value: 'Rajasthan', label: 'राजस्थान' },
    { value: 'Gujarat', label: 'गुजरात' },
    { value: 'Bihar', label: 'बिहार' },
    { value: 'Chhattisgarh', label: 'छत्तीसगढ़' },
    { value: 'Other', label: 'अन्य' }
  ];

  // District options based on selected state
  const districtOptions = districts.map(d => ({ value: d, label: d }));
  const permDistrictOptions = permDistricts.map(d => ({ value: d, label: d }));

  const guardianRelationOptions = [
    { value: 'grandfather', label: 'दादा/नाना' },
    { value: 'grandmother', label: 'दादी/नानी' },
    { value: 'uncle', label: 'चाचा/मामा' },
    { value: 'aunt', label: 'चाची/मामी' },
    { value: 'sibling', label: 'भाई/बहन' },
    { value: 'other', label: 'अन्य' }
  ];

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      {/* Tab Navigation */}
      <div className="sticky top-0 bg-white z-10 border-b mb-4">
        <div className="flex overflow-x-auto pb-2 gap-1">
          {tabs.map((tab, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setActiveTab(index)}
              className={`px-3 py-2 text-sm whitespace-nowrap rounded-lg transition ${
                activeTab === index 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="mr-1">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Tab 0: Basic Info */}
        {activeTab === 0 && (
          <div className="space-y-4">
            <FormSection title="पंजीकरण विवरण (Registration Details)">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="प्रवेश क्रमांक (Admission No.)" name="admission_number" value={formData.admission_number} onChange={handleChange} required placeholder="जैसे: 2025001" />
                <InputField label="रोल नंबर (Roll Number)" name="roll_number" value={formData.roll_number} onChange={handleChange} placeholder="कक्षा रोल नंबर" />
                <InputField label="स्कॉलर नंबर (Scholar No.)" name="scholar_number" value={formData.scholar_number} onChange={handleChange} placeholder="विद्यालय स्कॉलर नंबर" />
                <InputField label="नामांकन संख्या (Enrollment No.)" name="enrollment_number" value={formData.enrollment_number} onChange={handleChange} placeholder="बोर्ड नामांकन संख्या" />
              </div>
            </FormSection>

            <FormSection title="व्यक्तिगत विवरण (Personal Details)">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="नाम (अंग्रेजी में)" name="name" value={formData.name} onChange={handleChange} required placeholder="Student Name" />
                <InputField label="नाम (हिंदी में)" name="name_hindi" value={formData.name_hindi} onChange={handleChange} placeholder="विद्यार्थी का नाम" />
                <InputField label="लिंग (Gender)" name="gender" value={formData.gender} onChange={handleChange} options={genderOptions} required />
                <InputField label="जन्म तिथि (DOB)" name="dob" value={formData.dob} onChange={handleChange} type="date" required />
                <InputField label="रक्त समूह (Blood Group)" name="blood_group" value={formData.blood_group} onChange={handleChange} options={bloodGroupOptions} />
                <InputField label="मातृभाषा (Mother Tongue)" name="mother_tongue" value={formData.mother_tongue} onChange={handleChange} placeholder="जैसे: हिंदी" />
                <InputField label="राष्ट्रीयता (Nationality)" name="nationality" value={formData.nationality} onChange={handleChange} />
                <InputField label="जन्म स्थान (Birth Place)" name="place_of_birth" value={formData.place_of_birth} onChange={handleChange} placeholder="गांव/शहर" />
                <InputField label="जन्म जिला (Birth District)" name="birth_place_district" value={formData.birth_place_district} onChange={handleChange} />
                <InputField label="जन्म राज्य (Birth State)" name="birth_place_state" value={formData.birth_place_state} onChange={handleChange} options={stateOptions} />
              </div>
            </FormSection>

            <FormSection title="कक्षा विवरण (Class Details)">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField 
                  label="कक्षा (Class)" 
                  name="current_class_id" 
                  value={formData.current_class_id} 
                  onChange={handleChange} 
                  options={classes.map(c => ({ value: c.id, label: c.name }))} 
                  required 
                />
                <InputField 
                  label="सेक्शन (Section)" 
                  name="current_section_id" 
                  value={formData.current_section_id} 
                  onChange={handleChange} 
                  options={sections.map(s => ({ value: s.id, label: `Section ${s.name}` }))} 
                  required 
                />
                <InputField label="शैक्षणिक वर्ष (Academic Year)" name="academic_year" value={formData.academic_year} onChange={handleChange} placeholder="2025-26" required />
                <InputField label="माध्यम (Medium)" name="medium" value={formData.medium} onChange={handleChange} options={mediumOptions} />
                <InputField label="संकाय (Stream)" name="stream" value={formData.stream} onChange={handleChange} options={streamOptions} />
                <InputField label="द्वितीय भाषा (2nd Language)" name="second_language" value={formData.second_language} onChange={handleChange} options={languageOptions} />
                <InputField label="तृतीय भाषा (3rd Language)" name="third_language" value={formData.third_language} onChange={handleChange} options={languageOptions} />
                <InputField label="प्रवेश कक्षा (Admission Class)" name="admission_class" value={formData.admission_class} onChange={handleChange} placeholder="जिस कक्षा में पहले प्रवेश लिया" />
              </div>
            </FormSection>
          </div>
        )}

        {/* Tab 1: Identity & Category */}
        {activeTab === 1 && (
          <div className="space-y-4">
            <FormSection title="पहचान व सरकारी लिंकेज (Identity & Government Linkage)">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="समग्र आईडी (Samagra ID)" name="samagra_id" value={formData.samagra_id} onChange={handleChange} placeholder="9 अंकों की समग्र आईडी" />
                <InputField label="परिवार समग्र आईडी" name="samagra_family_id" value={formData.samagra_family_id} onChange={handleChange} placeholder="परिवार की समग्र आईडी" />
                <InputField label="आधार नंबर (Aadhaar)" name="aadhaar_number" value={formData.aadhaar_number} onChange={handleChange} placeholder="12 अंकों का आधार नंबर" />
                <InputField label="राशन कार्ड नंबर" name="ration_card_number" value={formData.ration_card_number} onChange={handleChange} />
                <InputField label="मूल निवास प्रमाण पत्र नंबर (Domicile)" name="domicile_certificate_number" value={formData.domicile_certificate_number} onChange={handleChange} placeholder="Domicile Certificate Number" />
              </div>
            </FormSection>

            <FormSection title="श्रेणी विवरण (Category Details)">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="धर्म (Religion)" name="religion" value={formData.religion} onChange={handleChange} options={religionOptions} />
                <InputField label="जाति (Caste)" name="caste" value={formData.caste} onChange={handleChange} placeholder="जाति का नाम" />
                <InputField label="वर्ग (Category)" name="caste_category" value={formData.caste_category} onChange={handleChange} options={categoryOptions} />
                <InputField label="उप-जाति (Sub-Caste)" name="sub_caste" value={formData.sub_caste} onChange={handleChange} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <InputField label="BPL परिवार" name="is_bpl" value={formData.is_bpl} onChange={handleChange} type="checkbox" />
                <InputField label="अल्पसंख्यक (Minority)" name="is_minority" value={formData.is_minority} onChange={handleChange} type="checkbox" />
                <InputField label="अनाथ (Orphan)" name="is_orphan" value={formData.is_orphan} onChange={handleChange} type="checkbox" />
                <InputField label="एकल अभिभावक" name="is_single_parent" value={formData.is_single_parent} onChange={handleChange} type="checkbox" />
              </div>
              {formData.is_bpl && (
                <div className="mt-4">
                  <InputField label="BPL कार्ड नंबर" name="bpl_card_number" value={formData.bpl_card_number} onChange={handleChange} />
                </div>
              )}
              {formData.is_minority && (
                <div className="mt-4">
                  <InputField label="अल्पसंख्यक प्रकार" name="minority_type" value={formData.minority_type} onChange={handleChange} options={[
                    { value: 'religious', label: 'धार्मिक (Religious)' },
                    { value: 'linguistic', label: 'भाषाई (Linguistic)' }
                  ]} />
                </div>
              )}
              {formData.is_single_parent && (
                <div className="mt-4">
                  <InputField label="एकल अभिभावक प्रकार" name="single_parent_type" value={formData.single_parent_type} onChange={handleChange} options={[
                    { value: 'father', label: 'पिता (Father Only)' },
                    { value: 'mother', label: 'माता (Mother Only)' }
                  ]} />
                </div>
              )}
            </FormSection>

            <FormSection title="विशेष आवश्यकता (CWSN / Divyang)">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputField label="विशेष आवश्यकता वाला बालक (CWSN)" name="is_cwsn" value={formData.is_cwsn} onChange={handleChange} type="checkbox" />
                {formData.is_cwsn && (
                  <>
                    <InputField label="विकलांगता का प्रकार" name="disability_type" value={formData.disability_type} onChange={handleChange} options={disabilityOptions} />
                    <InputField label="विकलांगता प्रतिशत (%)" name="disability_percentage" value={formData.disability_percentage} onChange={handleChange} type="number" placeholder="0-100" />
                  </>
                )}
              </div>
            </FormSection>
          </div>
        )}

        {/* Tab 2: Academic */}
        {activeTab === 2 && (
          <div className="space-y-4">
            <FormSection title="RTE एवं पुनरावर्ती स्थिति">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InputField label="RTE के तहत प्रवेश" name="rte_status" value={formData.rte_status} onChange={handleChange} type="checkbox" />
                <InputField label="पुनरावर्ती (Repeater)" name="is_repeater" value={formData.is_repeater} onChange={handleChange} type="checkbox" />
              </div>
            </FormSection>

            <FormSection title="पिछले विद्यालय का विवरण (Previous School Details)">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="पिछले विद्यालय का नाम" name="previous_school_name" value={formData.previous_school_name} onChange={handleChange} />
                <InputField label="पिछले विद्यालय का UDISE कोड" name="previous_school_udise" value={formData.previous_school_udise} onChange={handleChange} />
                <InputField label="पिछले विद्यालय का पता" name="previous_school_address" value={formData.previous_school_address} onChange={handleChange} />
                <InputField label="पिछली कक्षा" name="previous_class" value={formData.previous_class} onChange={handleChange} />
                <InputField label="पिछले वर्ष की स्थिति" name="previous_year_status" value={formData.previous_year_status} onChange={handleChange} options={previousStatusOptions} />
                <InputField label="पिछले वर्ष का प्रतिशत (%)" name="previous_year_percentage" value={formData.previous_year_percentage} onChange={handleChange} type="number" placeholder="0-100" />
                <InputField label="TC नंबर" name="tc_number" value={formData.tc_number} onChange={handleChange} placeholder="Transfer Certificate Number" />
              </div>
            </FormSection>
          </div>
        )}

        {/* Tab 3: Parents/Guardian */}
        {activeTab === 3 && (
          <div className="space-y-4">
            <FormSection title="पिता का विवरण (Father's Details)">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="पिता का नाम (अंग्रेजी)" name="father_name" value={formData.father_name} onChange={handleChange} placeholder="Father's Name" />
                <InputField label="पिता का नाम (हिंदी)" name="father_name_hindi" value={formData.father_name_hindi} onChange={handleChange} />
                <InputField label="व्यवसाय (Occupation)" name="father_occupation" value={formData.father_occupation} onChange={handleChange} />
                <InputField label="शिक्षा (Education)" name="father_education" value={formData.father_education} onChange={handleChange} options={educationOptions} />
                <InputField label="मोबाइल नंबर" name="father_phone" value={formData.father_phone} onChange={handleChange} type="tel" />
                <InputField label="आधार नंबर" name="father_aadhaar" value={formData.father_aadhaar} onChange={handleChange} />
                <InputField label="वार्षिक आय (रुपये में)" name="father_annual_income" value={formData.father_annual_income} onChange={handleChange} type="number" />
                <InputField label="जीवित हैं" name="father_alive" value={formData.father_alive} onChange={handleChange} type="checkbox" />
              </div>
            </FormSection>

            <FormSection title="माता का विवरण (Mother's Details)">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="माता का नाम (अंग्रेजी)" name="mother_name" value={formData.mother_name} onChange={handleChange} placeholder="Mother's Name" />
                <InputField label="माता का नाम (हिंदी)" name="mother_name_hindi" value={formData.mother_name_hindi} onChange={handleChange} />
                <InputField label="व्यवसाय (Occupation)" name="mother_occupation" value={formData.mother_occupation} onChange={handleChange} />
                <InputField label="शिक्षा (Education)" name="mother_education" value={formData.mother_education} onChange={handleChange} options={educationOptions} />
                <InputField label="मोबाइल नंबर" name="mother_phone" value={formData.mother_phone} onChange={handleChange} type="tel" />
                <InputField label="आधार नंबर" name="mother_aadhaar" value={formData.mother_aadhaar} onChange={handleChange} />
                <InputField label="वार्षिक आय (रुपये में)" name="mother_annual_income" value={formData.mother_annual_income} onChange={handleChange} type="number" />
                <InputField label="जीवित हैं" name="mother_alive" value={formData.mother_alive} onChange={handleChange} type="checkbox" />
              </div>
            </FormSection>

            <FormSection title="अभिभावक का विवरण (Guardian's Details - यदि माता-पिता से भिन्न हो)" defaultOpen={false}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="अभिभावक का नाम (अंग्रेजी)" name="guardian_name" value={formData.guardian_name} onChange={handleChange} />
                <InputField label="अभिभावक का नाम (हिंदी)" name="guardian_name_hindi" value={formData.guardian_name_hindi} onChange={handleChange} />
                <InputField label="संबंध (Relation)" name="guardian_relation" value={formData.guardian_relation} onChange={handleChange} options={guardianRelationOptions} />
                <InputField label="मोबाइल नंबर" name="guardian_phone" value={formData.guardian_phone} onChange={handleChange} type="tel" />
                <InputField label="व्यवसाय" name="guardian_occupation" value={formData.guardian_occupation} onChange={handleChange} />
                <InputField label="आधार नंबर" name="guardian_aadhaar" value={formData.guardian_aadhaar} onChange={handleChange} />
              </div>
            </FormSection>
          </div>
        )}

        {/* Tab 4: Contact & Address */}
        {activeTab === 4 && (
          <div className="space-y-4">
            <FormSection title="संपर्क विवरण (Contact Details)">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="प्राथमिक मोबाइल नंबर" name="primary_phone" value={formData.primary_phone} onChange={handleChange} type="tel" required placeholder="10 अंकों का मोबाइल नंबर" />
                <InputField label="द्वितीयक मोबाइल नंबर" name="secondary_phone" value={formData.secondary_phone} onChange={handleChange} type="tel" />
                <InputField label="ईमेल (Email)" name="email" value={formData.email} onChange={handleChange} type="email" />
                <InputField label="व्हाट्सएप नंबर" name="whatsapp_number" value={formData.whatsapp_number} onChange={handleChange} type="tel" />
              </div>
            </FormSection>

            <FormSection title="आपातकालीन संपर्क (Emergency Contact)">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputField label="नाम" name="emergency_contact_name" value={formData.emergency_contact_name} onChange={handleChange} />
                <InputField label="मोबाइल नंबर" name="emergency_contact_phone" value={formData.emergency_contact_phone} onChange={handleChange} type="tel" />
                <InputField label="संबंध" name="emergency_contact_relation" value={formData.emergency_contact_relation} onChange={handleChange} />
              </div>
            </FormSection>

            <FormSection title="वर्तमान पता (Current Address)">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="मकान नंबर" name="address_house_number" value={formData.address_house_number} onChange={handleChange} />
                <InputField label="गली/मोहल्ला" name="address_street_mohalla" value={formData.address_street_mohalla} onChange={handleChange} />
                <InputField label="गांव/नगर" name="address_village_town" value={formData.address_village_town} onChange={handleChange} required />
                <InputField label="डाकघर (Post Office)" name="address_post_office" value={formData.address_post_office} onChange={handleChange} />
                <InputField label="थाना (Police Station)" name="address_police_station" value={formData.address_police_station} onChange={handleChange} />
                <InputField label="ब्लॉक/तहसील" name="address_block_tehsil" value={formData.address_block_tehsil} onChange={handleChange} />
                <InputField label="राज्य (State)" name="address_state" value={formData.address_state} onChange={handleChange} options={stateOptions} required />
                <InputField label="जिला (District)" name="address_district" value={formData.address_district} onChange={handleChange} options={districtOptions.length > 0 ? districtOptions : null} required placeholder={districtOptions.length === 0 ? "पहले राज्य चुनें" : ""} />
                <InputField label="पिन कोड" name="address_pincode" value={formData.address_pincode} onChange={handleChange} required placeholder="6 अंकों का पिन कोड" />
              </div>
            </FormSection>

            <div className="px-4">
              <InputField label="स्थायी पता वर्तमान पते के समान है" name="is_address_same" value={formData.is_address_same} onChange={handleChange} type="checkbox" />
            </div>

            {!formData.is_address_same && (
              <FormSection title="स्थायी पता (Permanent Address)">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <InputField label="मकान नंबर" name="perm_house_number" value={formData.perm_house_number} onChange={handleChange} />
                  <InputField label="गली/मोहल्ला" name="perm_street_mohalla" value={formData.perm_street_mohalla} onChange={handleChange} />
                  <InputField label="गांव/नगर" name="perm_village_town" value={formData.perm_village_town} onChange={handleChange} />
                  <InputField label="डाकघर" name="perm_post_office" value={formData.perm_post_office} onChange={handleChange} />
                  <InputField label="थाना" name="perm_police_station" value={formData.perm_police_station} onChange={handleChange} />
                  <InputField label="ब्लॉक/तहसील" name="perm_block_tehsil" value={formData.perm_block_tehsil} onChange={handleChange} />
                  <InputField label="राज्य" name="perm_state" value={formData.perm_state} onChange={handleChange} options={stateOptions} />
                  <InputField label="जिला" name="perm_district" value={formData.perm_district} onChange={handleChange} options={permDistrictOptions.length > 0 ? permDistrictOptions : null} />
                  <InputField label="पिन कोड" name="perm_pincode" value={formData.perm_pincode} onChange={handleChange} />
                </div>
              </FormSection>
            )}
          </div>
        )}

        {/* Tab 5: Bank & Facilities */}
        {activeTab === 5 && (
          <div className="space-y-4">
            <FormSection title="बैंक विवरण (Bank Details - छात्रवृत्ति हेतु)">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="खाताधारक का नाम" name="bank_account_holder" value={formData.bank_account_holder} onChange={handleChange} />
                <InputField label="खाता संख्या" name="bank_account_number" value={formData.bank_account_number} onChange={handleChange} />
                <InputField label="IFSC कोड" name="bank_ifsc" value={formData.bank_ifsc} onChange={handleChange} placeholder="जैसे: SBIN0001234" />
                <InputField label="बैंक का नाम" name="bank_name" value={formData.bank_name} onChange={handleChange} />
                <InputField label="शाखा का नाम" name="bank_branch" value={formData.bank_branch} onChange={handleChange} />
              </div>
            </FormSection>

            <FormSection title="सुविधाएं (Facilities)">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InputField label="परिवहन सुविधा" name="transport_availed" value={formData.transport_availed} onChange={handleChange} type="checkbox" />
                <InputField label="मध्याह्न भोजन" name="mid_day_meal" value={formData.mid_day_meal} onChange={handleChange} type="checkbox" />
                <InputField label="निःशुल्क पाठ्यपुस्तकें" name="free_textbook" value={formData.free_textbook} onChange={handleChange} type="checkbox" />
                <InputField label="निःशुल्क यूनिफॉर्म" name="free_uniform" value={formData.free_uniform} onChange={handleChange} type="checkbox" />
                <InputField label="छात्रावास" name="hostel" value={formData.hostel} onChange={handleChange} type="checkbox" />
              </div>
              {formData.transport_availed && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <InputField label="विद्यालय से दूरी (किमी)" name="transport_distance_km" value={formData.transport_distance_km} onChange={handleChange} type="number" />
                  <InputField label="वाहन नंबर" name="transport_vehicle_number" value={formData.transport_vehicle_number} onChange={handleChange} />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <InputField label="छात्रवृत्ति का नाम" name="scholarship_name" value={formData.scholarship_name} onChange={handleChange} placeholder="जैसे: प्रतिभा किरण" />
                <InputField label="छात्रवृत्ति राशि (रुपये)" name="scholarship_amount" value={formData.scholarship_amount} onChange={handleChange} type="number" />
              </div>
            </FormSection>

            <FormSection title="स्वास्थ्य विवरण (Health Details)" defaultOpen={false}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputField label="ऊंचाई (सेमी)" name="height_cm" value={formData.height_cm} onChange={handleChange} type="number" />
                <InputField label="वजन (किग्रा)" name="weight_kg" value={formData.weight_kg} onChange={handleChange} type="number" />
                <InputField label="पहचान चिह्न 1" name="identification_mark_1" value={formData.identification_mark_1} onChange={handleChange} />
                <InputField label="पहचान चिह्न 2" name="identification_mark_2" value={formData.identification_mark_2} onChange={handleChange} />
                <InputField label="एलर्जी (यदि कोई हो)" name="allergies" value={formData.allergies} onChange={handleChange} />
                <InputField label="चिकित्सा स्थिति" name="medical_conditions" value={formData.medical_conditions} onChange={handleChange} />
              </div>
            </FormSection>
          </div>
        )}

        {/* Tab 6: Documents */}
        {activeTab === 6 && (
          <div className="space-y-4">
            <FormSection title="दस्तावेज़ विवरण (Documents)">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                  <InputField label="फोटो अपलोड किया" name="photo_uploaded" value={formData.photo_uploaded} onChange={handleChange} type="checkbox" />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                  <InputField label="जन्म प्रमाण पत्र" name="birth_certificate" value={formData.birth_certificate} onChange={handleChange} type="checkbox" />
                  {formData.birth_certificate && (
                    <InputField label="जन्म प्रमाण पत्र नंबर" name="birth_certificate_number" value={formData.birth_certificate_number} onChange={handleChange} />
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                  <InputField label="जाति प्रमाण पत्र" name="caste_certificate" value={formData.caste_certificate} onChange={handleChange} type="checkbox" />
                  {formData.caste_certificate && (
                    <InputField label="जाति प्रमाण पत्र नंबर" name="caste_certificate_number" value={formData.caste_certificate_number} onChange={handleChange} />
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                  <InputField label="आय प्रमाण पत्र" name="income_certificate" value={formData.income_certificate} onChange={handleChange} type="checkbox" />
                  {formData.income_certificate && (
                    <InputField label="आय प्रमाण पत्र नंबर" name="income_certificate_number" value={formData.income_certificate_number} onChange={handleChange} />
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                  <InputField label="मूल निवास प्रमाण पत्र (Domicile)" name="domicile_certificate" value={formData.domicile_certificate} onChange={handleChange} type="checkbox" />
                  {formData.domicile_certificate && (
                    <InputField label="मूल निवास प्रमाण पत्र नंबर" name="domicile_certificate_number" value={formData.domicile_certificate_number} onChange={handleChange} />
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                  <InputField label="विकलांगता प्रमाण पत्र" name="disability_certificate" value={formData.disability_certificate} onChange={handleChange} type="checkbox" />
                  {formData.disability_certificate && (
                    <InputField label="विकलांगता प्रमाण पत्र नंबर" name="disability_certificate_number" value={formData.disability_certificate_number} onChange={handleChange} />
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                  <InputField label="स्थानांतरण प्रमाण पत्र (TC)" name="transfer_certificate" value={formData.transfer_certificate} onChange={handleChange} type="checkbox" />
                  {formData.transfer_certificate && (
                    <InputField label="TC नंबर" name="transfer_certificate_number" value={formData.transfer_certificate_number} onChange={handleChange} />
                  )}
                </div>
              </div>
            </FormSection>

            <FormSection title="अन्य टिप्पणी (Remarks)" defaultOpen={false}>
              <InputField label="टिप्पणी" name="remarks" value={formData.remarks} onChange={handleChange} type="textarea" placeholder="कोई अतिरिक्त जानकारी..." />
            </FormSection>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="sticky bottom-0 bg-white border-t pt-4 mt-6 flex justify-between items-center">
          <div className="flex gap-2">
            {activeTab > 0 && (
              <button
                type="button"
                onClick={() => setActiveTab(activeTab - 1)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                पिछला (Previous)
              </button>
            )}
          </div>
          
          <div className="flex gap-2">
            {activeTab < tabs.length - 1 ? (
              <button
                type="button"
                onClick={() => setActiveTab(activeTab + 1)}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              >
                अगला (Next)
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  रद्द करें (Cancel)
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'सहेजा जा रहा है...' : (editStudent ? 'अपडेट करें' : 'विद्यार्थी जोड़ें')}
                </button>
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
